import { Link } from 'react-router-dom'
import {
  Anchor,
  Badge,
  Box,
  Button,
  Container,
  Divider,
  Grid,
  Group,
  List,
  Paper,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title
} from '@mantine/core'
import { IconArrowRight, IconBrandTelegram, IconCircleCheck, IconSparkles } from '@tabler/icons-react'
import { Seo } from '../components/Seo'
import SiteFooter from '../components/SiteFooter'
import { BRAND_NAME, BRAND_SUBLINE, LOGO_URL } from '../branding'

const managerChatUrl = 'https://t.me/fravai_support_bot'

const clientSegments = [
  { title: 'Стартовые селлеры', tag: 'разовые задачи', volume: '1–3 карточки / мес', spend: '~300–600 ₽/мес', income: '90–180 ₽/мес' },
  { title: 'Рабочая группа', tag: 'основной сегмент', volume: '5–8 карточек / мес', spend: '~900–1600 ₽/мес', income: '270–480 ₽/мес' },
  { title: 'Активные магазины', tag: 'ключевые клиенты', volume: '10–18 карточек / мес', spend: '~1800–3200 ₽/мес', income: '540–960 ₽/мес' },
  { title: 'Агентства', tag: 'b2b', volume: '25–50 карточек / мес', spend: '~4500–9000 ₽/мес', income: '1350–2700 ₽/мес', note: '1 агентство = 5–8 обычных клиентов по объёму.' }
] as const

const revenueScenarios = [
  { clients: 20, income: '~14 000 ₽/мес' },
  { clients: 50, income: '~36 000 ₽/мес' },
  { clients: 100, income: '~75 000 ₽/мес' },
  { clients: 200, income: '~150 000 ₽/мес' },
  { clients: 300, income: '~225 000 ₽/мес' },
  { clients: 500, income: '~375 000 ₽/мес' }
] as const

const howSteps = [
  { title: 'Получите партнёрскую ссылку', desc: 'Напишите в Telegram — подключим кабинет, выдадим реф/UTM и базовые материалы.' },
  { title: 'Расскажите там, где у вас аудитория', desc: 'Канал, чат, рассылка, лендинг, вебинар. Дадим промо, примеры и подсказки под вашу нишу.' },
  { title: 'Получайте выплаты', desc: 'Комиссия до 30% с каждой оплаты, выплаты после холда.' }
]

const partnerPerks = [
  'Автоматический учёт рефералок, промокодов и оплат.',
  'Выплаты дважды в месяц, минималка 500 ₽, холд 7 дней.',
  'Готовые лендинги, баннеры, тексты, скрипты под звонки.',
  'Персональный менеджер и быстрый саппорт в Telegram.',
  'Прозрачные отчёты в кабинете: клики, регистрации, оплаты, удержание.'
]

export default function PartnerProgramPage() {
  return (
    <Box className="partner-page">
      <Seo
        title="Партнёрская программа Fravai"
        description="Зарабатывайте до 30% lifetime с каждой оплаты клиентов маркетплейсов. Выплаты дважды в месяц, готовые материалы и прозрачная аналитика."
        canonical="/partners"
      />

      <header className="topbar topbar--solid partner-topbar">
        <div className="partner-topbar__shell">
          <Container size="lg" py="xs" className="partner-topbar__bar">
            <Group justify="space-between" align="center">
              <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
                <Group gap="xs">
                  <img src={LOGO_URL} alt={`${BRAND_NAME} logo`} width={32} height={32} style={{ display: 'block' }} />
                  <div className="brand-lockup">
                    <span className="brand-wordmark">{BRAND_NAME}</span>
                    <span className="brand-subline">{BRAND_SUBLINE}</span>
                  </div>
                </Group>
              </Link>
              <Group>
                <Button component={Anchor} href="#model" variant="subtle" radius="xl" size="sm">Как зарабатывать</Button>
                <Button component={Anchor} href="#clients_details" variant="subtle" radius="xl" size="sm">Клиенты</Button>
                {/* <Button component={Anchor} href="#clients" variant="subtle" radius="xl" size="sm">Расчет дохода</Button> */}
                <Button component="a" href={managerChatUrl} target="_blank" rel="noreferrer" radius="xl" size="sm" className="topbar-cta">
                  Связаться
                </Button>
              </Group>
            </Group>
          </Container>
        </div>
      </header>

      <main>
        <section className="partner-hero">
          <Container size="lg">
            <Grid gutter="xl" align="center">
              <Grid.Col span={{ base: 12, md: 7 }}>
                <Stack gap="md">
                  <Group gap="xs">
                    <Badge variant="light" color="pink" size="lg">Партнёрка Fravai</Badge>
                    <Badge variant="light" color="cyan" size="lg">AI-платформа</Badge>
                  </Group>
                  <Title order={1} className="partner-hero__title">
                    Зарабатывайте до 30% от вашей аудитории
                  </Title>
                  <Text size="lg" c="rgba(255,255,255,0.86)" maw={640}>
                    Fravai создаёт карточки товаров и инфографику под Wildberries и Ozon за 30–60 секунд. Селлерам нужен свежий визуал каждый месяц — вы получаете стабильные комиссии с каждого платежа.
                  </Text>
                  <Group gap="md" mt="sm">
                    <Button
                      size="md"
                      radius="xl"
                      rightSection={<IconBrandTelegram size={18} />}
                      component="a"
                      href={managerChatUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Подключиться как партнёр
                    </Button>
                    <Button
                      size="md"
                      variant="outline"
                      radius="xl"
                      component={Link}
                      to="/legal/partner-terms"
                      rightSection={<IconArrowRight size={16} />}
                    >
                      Условия программы
                    </Button>
                  </Group>
                  <Group gap="lg" mt="sm" wrap="wrap">
                    <StatPill label="Комиссия" value="до 30%" />
                    <StatPill label="Выплаты" value="Быстрый вывод" />
                    <StatPill label="Старт" value="5 минут   " />
                  </Group>
                </Stack>
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 5 }}>
                <Paper radius="xl" p="lg" className="partner-hero__card" shadow="xl">
                  <Stack gap="xs">
                    <Text fw={700} size="lg">Почему селлеры платят регулярно</Text>
                    <List spacing={10} icon={<IconCircleCheck size={16} color="#22d3ee" />} c="#0f172a">
                      <List.Item>Карточки и инфографика напрямую влияют на CTR и продажи.</List.Item>
                      <List.Item>Нужны обновления: сезонность, акции, A/B тесты, новые товары.</List.Item>
                      <List.Item>Fravai закрывает цикл быстрее и дешевле дизайнера: 30–60 сек до результата.</List.Item>
                    </List>
                  </Stack>
                </Paper>
              </Grid.Col>
            </Grid>
          </Container>
        </section>

        <Section id="problem" title="Селлерам нужен визуал постоянно" subtitle="Карточки влияют на CTR и оборот, но классический путь через дизайнеров медленный и дорогой.">
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <PartnerCard title="Что делают селлеры каждый месяц" accent>
              <List icon={<IconCircleCheck size={16} color="#2563eb" />} spacing={8}>
                <List.Item>Обложки и инфографика для новых товаров.</List.Item>
                <List.Item>Обновления под акции, сезоны, распродажи.</List.Item>
                <List.Item>A/B тесты обложек и карточек.</List.Item>
                <List.Item>Доработки, чтобы поднять конверсию и CTR.</List.Item>
              </List>
            </PartnerCard>
            <PartnerCard title="Почему Fravai закрывает эту боль">
              <List icon={<IconCircleCheck size={16} color="#22d3ee" />} spacing={8}>
                <List.Item>30–60 секунд вместо дней ожидания дизайнера.</List.Item>
                <List.Item>Соблюдение требований маркетплейсов.</List.Item>
                <List.Item>Редактор для правок без привлечения подрядчиков.</List.Item>
                <List.Item>Готовые шаблоны для сезонных сценариев.</List.Item>
              </List>
              <Text size="sm" c="dimmed" mt="xs">
                Визуал нужен ежемесячно — именно поэтому комиссии партнёра повторяются.
              </Text>
            </PartnerCard>
          </SimpleGrid>
        </Section>

        <Section id="solution" title="Fravai: AI-платформа для визуала под маркетплейсы" subtitle="Закрываем весь цикл: обложка, инфографика, доработка и экспорт по стандартам WB и Ozon.">
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <PartnerCard title="Что умеет продукт">
              <List icon={<IconCircleCheck size={16} color="#2563eb" />} spacing={8}>
                <List.Item>Создаёт карточки товара за 30–60 секунд.</List.Item>
                <List.Item>Генерирует продающую инфографику.</List.Item>
                <List.Item>Редактор для точных правок и брендинга.</List.Item>
                <List.Item>Готовые пресеты под WB / Ozon.</List.Item>
              </List>
            </PartnerCard>
            <PartnerCard title="Что получает ваш клиент" accent>
              <List icon={<IconCircleCheck size={16} color="#f43f5e" />} spacing={8}>
                <List.Item>Быстрый запуск без дизайнеров.</List.Item>
                <List.Item>Гибкий расход кредитов под задачи.</List.Item>
                <List.Item>Постоянные A/B улучшения визуала.</List.Item>
                <List.Item>Рост CTR, конверсии и оборота.</List.Item>
              </List>
            </PartnerCard>
          </SimpleGrid>
        </Section>

        <Section id="model" title="Как вы зарабатываете" subtitle="До 30% с каждой оплаты клиентов — пока они пользуются сервисом. Выплаты дважды в месяц.">
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <PartnerCard title="Простая формула" accent>
              <List icon={<IconCircleCheck size={16} color="#f43f5e" />} spacing={8}>
                <List.Item>Вы приводите клиента по реферальной ссылке или промокоду.</List.Item>
                <List.Item>Клиент покупает тарифы и пополняет кредиты.</List.Item>
                <List.Item>Вы получаете до 30% с каждой оплаты клиента.</List.Item>
                <List.Item>Комиссии повторяются ежемесячно, пока клиент активен.</List.Item>
              </List>
            </PartnerCard>
            <PartnerCard>
              <Stack gap={6}>
                <Text size="sm" tt="uppercase" fw={600} c="dimmed" letterSpacing={1}>Экономика</Text>
                <Title order={2} className="partner-highlight">до 30%</Title>
                <Text size="sm" c="dimmed">
                  Получайте от каждого пополнения привлеченных клиентов
                </Text>
              </Stack>
            </PartnerCard>
          </SimpleGrid>
        </Section>


        <Section id="clients_details" title="Кто пользуется Fravai и сколько тратит" subtitle="Реальные сценарии поведения клиентов. Видите свой рынок — планируйте доход.">
          <SimpleGrid cols={{ base: 1, md: 2, lg: 4 }} spacing="md">
            {clientSegments.map(seg => (
              <PartnerCard key={seg.title} title={seg.title} badge={seg.tag} accent={seg.title === 'Агентства'}>
                <Stack gap={6}>
                  <Text size="sm" c="dimmed">{seg.volume}</Text>
                  {/* <Text size="sm">Ваш доход: <Text component="span" fw={700}>{seg.income}</Text></Text> */}
                  {seg.note && <Text size="xs" c="dimmed">{seg.note}</Text>}
                </Stack>
              </PartnerCard>
            ))}
          </SimpleGrid>
        </Section>

        {/*
        <Section id="clients" title="Примеры ежемесячного дохода партнёра" subtitle="Считаем в реальных сценариях: сколько приносит ваша аудитория.">
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <PartnerCard accent>
              <Text size="sm" tt="uppercase" fw={600} c="dimmed" letterSpacing={1}>Сценарии</Text>
              <Stack gap={8} mt="xs">
                {revenueScenarios.map(item => (
                  <Group key={item.clients} justify="space-between" align="center">
                    <Text fw={700}>{item.clients} клиентов</Text>
                    <Text fw={600} c="var(--accent-2, #8b5cf6)">{item.income}</Text>
                  </Group>
                ))}
              </Stack>
            </PartnerCard>
            <PartnerCard title="Что важно понимать">
              <List icon={<IconCircleCheck size={16} color="#2563eb" />} spacing={8}>
                <List.Item>Доход зависит от количества клиентов, а не от вашего времени.</List.Item>
                <List.Item>Клиенты платят регулярно - вы получаете регулярно.</List.Item>
                <List.Item>Агентства дают кратный прирост оборота.</List.Item>
                <List.Item>Рынок маркетплейсов растёт - аудитории становится больше.</List.Item>
              </List>
              <Text size="sm" c="dimmed" mt="xs">
                Один раз подключили партнёрку - дальше работает эффект накопления.
              </Text>
            </PartnerCard>
          </SimpleGrid>
        </Section>
        */}


        <Section title="Почему партнёрка удобна" subtitle="Всё, что нужно, чтобы быстро запуститься и масштабироваться.">
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <PartnerCard title="Инструменты и материалы" accent>
              <List icon={<IconCircleCheck size={16} color="#f43f5e" />} spacing={8}>
                <List.Item>Удобный личный кабинет партнёра.</List.Item>
                <List.Item>Готовые лендинги, баннеры, тексты, цепочки писем.</List.Item>
                <List.Item>Персональный промокод и UTM, автоучёт кликов и оплат.</List.Item>
              </List>
            </PartnerCard>
            <PartnerCard title="Прозрачные выплаты">
              <List icon={<IconCircleCheck size={16} color="#22d3ee" />} spacing={8}>
                <List.Item>Возможность вывести каждый месяц.</List.Item>
                <List.Item>Детальные отчёты: регистрации, покупки, удержание.</List.Item>
              </List>
            </PartnerCard>
          </SimpleGrid>
        </Section>

        <Section title="3 шага, чтобы начать зарабатывать с Fravai">
          <SimpleGrid cols={{ base: 1, md: 3 }} spacing="lg">
            {howSteps.map((step, idx) => (
              <PartnerCard key={step.title} badge={`Шаг ${idx + 1}`} title={step.title}>
                <Text size="sm" c="dimmed">{step.desc}</Text>
              </PartnerCard>
            ))}
          </SimpleGrid>
          <Group gap="md" mt="lg">
            <Button
              size="md"
              radius="xl"
              component="a"
              href={managerChatUrl}
              target="_blank"
              rel="noreferrer"
              rightSection={<IconBrandTelegram size={18} />}
            >
              Написать в Telegram
            </Button>
            <Text size="sm" c="dimmed">Обсудим формат, дадим материалы и подключим кабинет.</Text>
          </Group>
        </Section>
      </main>

      <SiteFooter />
    </Box>
  )
}

function Section({ id, title, subtitle, children }: { id?: string; title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <section id={id} className="partner-section">
      <Container size="lg">
        <Stack gap="sm" mb="lg">
          <Title order={2}>{title}</Title>
          {subtitle && <Text size="lg" c="dimmed" maw={760}>{subtitle}</Text>}
          <Divider variant="dashed" />
        </Stack>
        {children}
      </Container>
    </section>
  )
}

function PartnerCard({ title, children, badge, accent }: { title?: string; children: React.ReactNode; badge?: string; accent?: boolean }) {
  return (
    <Paper radius="lg" p="lg" className={`partner-card ${accent ? 'partner-card--accent' : ''}`} shadow="md">
      <Stack gap="sm">
        {(title || badge) && (
          <Group gap="xs">
            {title && <Title order={4} className="partner-card__title">{title}</Title>}
            {badge && <Badge variant="light" color="blue" size="sm">{badge}</Badge>}
          </Group>
        )}
        <div>{children}</div>
      </Stack>
    </Paper>
  )
}

function StatPill({ label, value }: { label: string; value: string }) {
  return (
    <Paper radius="xl" p="sm" className="partner-stat">
      <Group gap="xs" style={{ minWidth: 160 }}>
        <IconSparkles size={16} color="#8b5cf6" />
        <div>
          <Text size="xs" c="var(--muted)" tt="uppercase" fw={600} letterSpacing={0.8}>{label}</Text>
          <Text fw={700} c="var(--text)">{value}</Text>
        </div>
      </Group>
    </Paper>
  )
}
